import * as React from 'react';

import { Tabs, TabsList, TabsTrigger } from './tabs';

interface NavTabItem {
  value: string;
  label: React.ReactNode;
}

interface NavTabsProps {
  value: string;
  onValueChange: (value: string) => void;
  items: NavTabItem[];
  size?: 'sm' | 'md';
  className?: string;
  children?: React.ReactNode;
}

// Radix Tabs wires arrow keys to a RovingFocusGroup, so once a trigger is focused
// (e.g. after a click) Left/Right/Up/Down hop focus between tabs. There is no
// Radix prop to disable this. These NavTabs sit alongside surfaces that own arrow
// keys for their own navigation (in Critical Summary: plain Left/Right steps through
// episodes, Cmd/Ctrl+Left/Right moves between transmissions), where a stray
// focus-hop reads as the tab strip "switching" under the user.
//
// Radix runs its roving handler via `composeEventHandlers(props.onKeyDown, …)`,
// which skips its own handler when the passed-in onKeyDown has already called
// preventDefault(). So calling preventDefault() for arrow keys on the trigger
// suppresses the focus hop WITHOUT stopping propagation — the event still bubbles
// to document, where the owning surface's arrow nav handles it. Tab/Enter/Space/click
// are untouched.
//
// IMPORTANT for the owning surface: because this preventDefault() fires on every
// arrow key, a document-level arrow handler that skips ALL `defaultPrevented`
// events would silently kill arrow navigation for as long as a tab trigger holds
// focus (i.e. right after the user clicks a tab). The right policy is to skip
// prevented events EXCEPT those originating inside a `[role="tablist"]` — that
// exception is this suppression; every other preventer genuinely consumed the key
// (see `isNavTabsRovingSuppression` in the Critical Summary's
// use-episode-arrow-keys.ts for the reference implementation).
const ROVING_ARROW_KEYS = new Set([
  'ArrowLeft',
  'ArrowRight',
  'ArrowUp',
  'ArrowDown',
]);

const suppressArrowRoving = (event: React.KeyboardEvent<HTMLButtonElement>) => {
  if (ROVING_ARROW_KEYS.has(event.key)) {
    event.preventDefault();
  }
};

export const NavTabs = ({
  value,
  onValueChange,
  items,
  size = 'md',
  className,
  children,
}: NavTabsProps) => {
  const listStyles =
    size === 'sm'
      ? 'gap-2 rounded-md border border-gray-200 bg-card p-1 h-auto text-foreground justify-start w-fit'
      : 'gap-2 rounded-md border border-border p-1 h-11 fhd:h-12 text-foreground justify-start w-fit';

  const triggerStyles =
    size === 'sm'
      ? 'rounded-md px-3 fhd:px-4 py-1.5 fhd:py-2 text-base leading-6 font-normal text-center transition-colors data-[state=active]:font-bold data-[state=active]:shadow-none data-[state=active]:bg-primary/10 data-[state=active]:text-gray-800 data-[state=active]:hover:bg-primary/10 data-[state=inactive]:bg-card data-[state=inactive]:text-gray-800 data-[state=inactive]:hover:bg-gray-50'
      : 'rounded-t-md px-3 fhd:px-4 py-1.5 fhd:py-2 text-base font-normal h-9 fhd:h-10 text-center data-[state=active]:font-bold data-[state=active]:shadow-none data-[state=active]:bg-primary/10 data-[state=active]:text-foreground data-[state=inactive]:bg-transparent data-[state=inactive]:text-muted-foreground';

  return (
    <Tabs value={value} onValueChange={onValueChange} className={className}>
      <TabsList className={listStyles}>
        {items.map((item) => (
          <TabsTrigger
            key={item.value}
            value={item.value}
            className={triggerStyles}
            onKeyDown={suppressArrowRoving}
          >
            {item.label}
          </TabsTrigger>
        ))}
      </TabsList>
      {children}
    </Tabs>
  );
};
